class ProductPersonalization extends HTMLElement {
  connectedCallback() {
    this.nameInput = this.querySelector('[data-personalization-name]');
    this.colourInputs = this.querySelectorAll('[data-personalization-colour]');
    this.selectedColourLabel = this.querySelector('[data-personalization-selected-colour]');
    this.previewCard = this.querySelector('[data-personalization-preview-card]');
    this.previewText = this.querySelector('[data-personalization-preview-text]');
    this.productColourLabel = this.querySelector('[data-product-colour-label]');
    this.renderRoot = this.closest('[data-product-grid-content]') || this.closest('.shopify-section') || document;

    if (this.colourInputs.length > 0 && this.dataset.initialSelectionApplied !== 'true') {
      this.colourInputs.forEach((input, index) => {
        input.checked = index === 0;
      });
      this.dataset.initialSelectionApplied = 'true';
    }

    this.nameInput?.addEventListener('input', this.handleInput);
    this.colourInputs.forEach((input) => input.addEventListener('change', this.updatePreview));
    this.renderRoot.addEventListener('change', this.handleProductOptionChange);

    this.mutationObserver = new MutationObserver(this.schedulePreviewUpdate);
    this.mutationObserver.observe(this.renderRoot, { childList: true, subtree: true });
    this.resizeObserver = new ResizeObserver(this.schedulePreviewUpdate);
    if (this.previewCard) this.resizeObserver.observe(this.previewCard);
    this.schedulePreviewUpdate();
    if (this.dataset.previewFont === 'authentic-signature') {
      document.fonts.load('400 72px "Authentic Signature"').then(() => {
        if (this.isConnected) this.schedulePreviewUpdate();
      }).catch(() => {});
    }
  }

  disconnectedCallback() {
    this.nameInput?.removeEventListener('input', this.handleInput);
    this.colourInputs?.forEach((input) => input.removeEventListener('change', this.updatePreview));
    this.renderRoot?.removeEventListener('change', this.handleProductOptionChange);
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
  }

  handleInput = () => {
    if (this.dataset.forceUppercase === 'true' && this.nameInput) {
      const selectionStart = this.nameInput.selectionStart;
      const selectionEnd = this.nameInput.selectionEnd;
      this.nameInput.value = this.nameInput.value.toUpperCase();
      this.nameInput.setSelectionRange(selectionStart, selectionEnd);
    }

    this.updatePreview();
  };

  handleProductOptionChange = (event) => {
    const optionName = event.target?.dataset?.optionName || event.target?.selectedOptions?.[0]?.dataset?.optionName;
    if (optionName && /colou?r/i.test(optionName)) this.schedulePreviewUpdate();
  };

  schedulePreviewUpdate = () => {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(this.updatePreview);
  };

  getFontFamily() {
    if (this.dataset.previewFont === 'authentic-signature') return '"Authentic Signature", cursive';
    if (this.dataset.previewFont === 'heading') return 'var(--font-heading--family)';
    if (this.dataset.previewFont === 'body') return 'var(--font-body--family)';
    return 'var(--font-accent--family)';
  }

  getPreviewColour() {
    const selectedColour = this.querySelector('[data-personalization-colour]:checked');
    if (selectedColour?.dataset.previewColor) return selectedColour.dataset.previewColor;

    const variantYarnColour = this.getSelectedVariantYarnColour();
    if (variantYarnColour) return this.getYarnPreviewColour(variantYarnColour);

    return this.dataset.defaultPreviewColor || '#d48ca4';
  }

  getSelectedVariantValue(optionNameMatches) {
    const checkedOptions = this.renderRoot.querySelectorAll('variant-picker input[data-option-name]:checked');
    for (const option of checkedOptions) {
      if (optionNameMatches(option.dataset.optionName || '')) return option.value;
    }

    const selects = this.renderRoot.querySelectorAll('variant-picker select');
    for (const select of selects) {
      const selectedOption = select.selectedOptions?.[0];
      const optionName = selectedOption?.dataset.optionName || select.name || '';
      if (optionNameMatches(optionName)) return select.value;
    }

    return '';
  }

  isProductColourOption(optionName) {
    const normalized = optionName.replace(/^options\[/i, '').replace(/\]$/, '').trim();
    return /^(product\s+)?colou?r$/i.test(normalized);
  }

  isYarnColourOption(optionName) {
    const normalized = optionName.replace(/^options\[/i, '').replace(/\]$/, '').trim();
    return /colou?r/i.test(normalized) && /(embroidery|yarn|name)/i.test(normalized);
  }

  getSelectedVariantYarnColour() {
    return this.getSelectedVariantValue((optionName) => this.isYarnColourOption(optionName));
  }

  getYarnPreviewColour(colourName) {
    const normalized = colourName.toLowerCase().trim();
    const colours = {
      'baby pink': '#f4c2d0',
      black: '#1b1b1b',
      blue: '#0875af',
      'cornflower blue': '#a8c7eb',
      'dusky pink': '#c9828d',
      'dusty pink': '#c9828d',
      green: '#008f7d',
      'lemon yellow': '#e5aa37',
      lilac: '#bdb1d8',
      'mint green': '#62ad93',
      'muddy green': '#788069',
      oatmeal: '#cbb79f',
      'off white': '#f6eed6',
      'orange pink': '#d88978',
      peach: '#e7b69e',
      red: '#c7363a',
      'pink purple': '#c50078',
      'soft pink': '#f05b91',
      yellow: '#e5aa37',
    };

    if (normalized.includes('rainbow')) return 'rainbow';
    return colours[normalized] || this.dataset.defaultPreviewColor || '#d48ca4';
  }

  getSelectedProductColour() {
    return (
      this.getSelectedVariantValue((optionName) => this.isProductColourOption(optionName)) ||
      this.dataset.initialProductColor ||
      ''
    );
  }

  getBackground(colourName) {
    const normalized = colourName.toLowerCase().replace(/^\d+\s*/, '').trim();
    const colours = {
      beige: '#cfb897',
      black: '#252525',
      blue: '#6f93b5',
      'blush pink': '#ddb2b2',
      'cornflower blue': '#8fa9d1',
      cream: '#eee5cf',
      'cream speckled': '#d9ccb6',
      'duck egg': '#a9c7bf',
      'duck egg green': '#a9c7bf',
      'duckegg green': '#a9c7bf',
      'dusky pink': '#c9979d',
      'dusty pink': '#c9979d',
      ecru: '#e3d5b8',
      'ecru/off white': 'linear-gradient(135deg, #e3d5b8 0 50%, #f3efe5 50% 100%)',
      gold: '#c9a45b',
      green: '#7e9a79',
      grey: '#aaa9a6',
      'lemon yellow': '#eadf82',
      'light blue': '#a8c7eb',
      lilac: '#bba9cc',
      'mint green': '#a9cbb9',
      'muddy green': '#788069',
      'mustard gold': '#c59a3e',
      oatmeal: '#c7b597',
      'off white': '#f3efe5',
      'orange pink': 'linear-gradient(135deg, #d9955c 0 50%, #d990a4 50% 100%)',
      peach: '#e7b69e',
      pink: '#dca6b4',
      'pink purple': '#aa85ad',
      red: '#b85c5c',
      'sage green': '#a5ad91',
      silver: '#babdc0',
      'silver grey': '#afb3b5',
      'soft pink': '#e1b4bf',
      'speckled beige': '#c8b598',
      teal: '#4f8d8b',
      white: '#f7f7f4',
      yellow: '#e4cf63',
    };

    if (normalized.includes('rainbow')) {
      return 'linear-gradient(135deg, #e7a2ad, #e6c47c, #9dbc94, #91afd0, #b49bc5)';
    }

    if (normalized === 'blush/beige') {
      return 'linear-gradient(135deg, #ddb2b2 0 50%, #cfb897 50% 100%)';
    }

    return colours[normalized] || this.dataset.defaultBackgroundColor || '#eee7dc';
  }

  fitPreviewText() {
    if (!this.previewText) return;

    this.previewText.style.removeProperty('--personalization-fitted-font-size');

    const availableWidth = this.previewText.clientWidth;
    const renderedWidth = this.previewText.scrollWidth;
    if (!availableWidth || renderedWidth <= availableWidth) return;

    const defaultSize = Number.parseFloat(getComputedStyle(this.previewText).fontSize);
    const fittedSize = Math.max(18, Math.floor(defaultSize * (availableWidth / renderedWidth)));
    this.previewText.style.setProperty('--personalization-fitted-font-size', `${fittedSize}px`);
  }

  updatePreview = () => {
    if (!this.previewCard || !this.previewText) return;

    const enteredName = this.nameInput?.value.trim() || '';
    const previewText = enteredName || this.dataset.previewPlaceholder || 'Your name';
    const previewColour = this.getPreviewColour();
    const productColour = this.getSelectedProductColour();
    const selectedYarnColour = this.querySelector('[data-personalization-colour]:checked')?.value || '';

    if (this.previewText.textContent !== previewText) this.previewText.textContent = previewText;
    this.previewText.dataset.empty = String(enteredName.length === 0);
    this.previewText.dataset.rainbow = String(previewColour === 'rainbow');
    this.previewCard.style.setProperty('--personalization-preview-font', this.getFontFamily());
    this.previewCard.style.setProperty('--personalization-preview-background', this.getBackground(productColour));

    if (this.productColourLabel) {
      const colourLabel = productColour ? ` ${productColour}` : '';
      if (this.productColourLabel.textContent !== colourLabel) {
        this.productColourLabel.textContent = colourLabel;
      }
    }

    if (this.selectedColourLabel && this.selectedColourLabel.textContent !== selectedYarnColour) {
      this.selectedColourLabel.textContent = selectedYarnColour;
    }

    if (previewColour === 'rainbow') {
      this.previewCard.style.removeProperty('--personalization-preview-color');
    } else {
      this.previewCard.style.setProperty('--personalization-preview-color', previewColour);
    }

    this.fitPreviewText();
  };
}

if (!customElements.get('product-personalization')) {
  customElements.define('product-personalization', ProductPersonalization);
}
